import { createElement, useCallback, useEffect, useMemo, useState } from 'react';
import { getI18n } from 'react-i18next';

import { ITableCell, ITableRow } from '../../../components/app';
import {
  DevicesSort,
  OrganizationDevicePatientRow,
  PatientSort,
} from '../../../components/app/organization/organization-devices/components';
import { DeviceMenu } from '../../../components/app/organization/organization-devices/components/device-menu';
import { ORGANIZATION_DEVICES_MAX_ROWS } from '../../../constants';
import { DevicesSortBy, FetchStatus, SortType } from '../../../enums';
import { IOrganizationDevice } from '../../../interfaces';
import { useDispatch, useSelector } from '../../../utils';
import { getPatientName } from '../../patients';
import {
  fetchOrganizationDevices,
  orderOrganizationDevices,
  registerOrganizationDevices,
  removeOrganizationDevice,
  resetOrderOrganizationDevicesStatus,
  resetRegisterOrganizationDevicesStatus,
  resetRemoveOrganizationDeviceStatus,
  updateOrganizationDevicesSearchText,
  updateOrganizationDevicesSort,
} from '../actions';
import { OrganizationDevicesTableColumns } from '../constants';
import { OrganizationDevicesTableColumn } from '../enums';
import {
  selectOrganizationDevices,
  selectOrganizationDevicesSearchText,
  selectOrganizationDevicesSort,
} from '../selectors';
import { IOrganizationDevicesSort } from '../types';
import { useOrganization } from './organization';

export const useOrganizationDevices = () => useSelector(selectOrganizationDevices);
export const useOrganizationDevicesSearchText = () => useSelector(selectOrganizationDevicesSearchText);
export const useOrganizationDevicesSort = () => useSelector(selectOrganizationDevicesSort);

export function useResetRemoveOrganizationDeviceStatus() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetRemoveOrganizationDeviceStatus());
  }, [dispatch]);
}

export function useResetRegisterOrganizationDevicesStatus() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetRegisterOrganizationDevicesStatus());
  }, [dispatch]);
}

export function useResetOrderOrganizationDevicesStatus() {
  const dispatch = useDispatch();

  return useCallback(() => {
    dispatch(resetOrderOrganizationDevicesStatus());
  }, [dispatch]);
}

export function useUpdateOrganizationDevicesSearchText() {
  const dispatch = useDispatch();

  return useCallback(
    (searchText?: string) => {
      dispatch(updateOrganizationDevicesSearchText({ searchText: searchText ?? '' }));
    },
    [dispatch],
  );
}

export function useUpdateOrganizationDevicesSort() {
  const dispatch = useDispatch();

  return useCallback(
    (sort: IOrganizationDevicesSort) => {
      dispatch(updateOrganizationDevicesSort({ sort }));
    },
    [dispatch],
  );
}

export function useFetchOrganizationDevices() {
  const dispatch = useDispatch();
  const organization = useOrganization();

  useEffect(() => {
    if (organization.status.fetchDevices === FetchStatus.IDLE) {
      dispatch(fetchOrganizationDevices());
    }
  }, []);
}

export function useRemoveOrganizationDevice() {
  const dispatch = useDispatch();

  return useCallback(
    (device: IOrganizationDevice) => {
      dispatch(removeOrganizationDevice({ device }));
    },
    [dispatch],
  );
}

export function useRegisterOrganizationDevices() {
  const dispatch = useDispatch();

  return useCallback(
    (invoiceNumber: string) => {
      dispatch(registerOrganizationDevices({ request: { invoiceNumber } }));
    },
    [dispatch],
  );
}

export function useOrderOrganizationDevice() {
  const dispatch = useDispatch();

  return useCallback(
    (count: number) => {
      dispatch(orderOrganizationDevices({ request: { count } }));
    },
    [dispatch],
  );
}

export function useOrganizationDevicesTableRows(maxRows: boolean): ITableRow[] {
  const devices = maxRows
    ? useOrganizationDevices().slice(0, ORGANIZATION_DEVICES_MAX_ROWS)
    : useFilteredOrganizationDevices();

  return useMemo(() => {
    return devices.map((device: IOrganizationDevice) => ({
      id: device.serialNumber,
      elements: OrganizationDevicesTableColumns.map(({ column, minWidth }) => {
        switch (column) {
          case OrganizationDevicesTableColumn.SERIAL_NUMBER:
            return {
              element: createElement('span', {}, device.serialNumber),
              minWidth: minWidth(!maxRows),
            };
          case OrganizationDevicesTableColumn.PATIENT:
            return {
              element: createElement(OrganizationDevicePatientRow, { patient: device.patient, clickable: !maxRows }),
              minWidth: minWidth(!maxRows),
            };
          case OrganizationDevicesTableColumn.MENU:
            return {
              element: maxRows ? createElement('span') : createElement(DeviceMenu, { device }),
              minWidth: minWidth(!maxRows),
            };
        }
      }),
    }));
  }, [devices]);
}

export function useOrganizationDevicesTableHeaders(sortable: boolean): ITableCell[] {
  return OrganizationDevicesTableColumns.map(({ column, minWidth }) => {
    const i18n = getI18n();
    switch (column) {
      case OrganizationDevicesTableColumn.SERIAL_NUMBER:
        return {
          element: sortable
            ? createElement(DevicesSort, { by: DevicesSortBy.SERIAL_NUMBER })
            : createElement('span', {}, i18n.t('organization.devices.table.serialNumber')),
          minWidth: minWidth(sortable),
        };
      case OrganizationDevicesTableColumn.PATIENT:
        return {
          element: sortable
            ? createElement(PatientSort, { by: DevicesSortBy.PATIENT })
            : createElement('span', {}, i18n.t('organization.devices.table.patient')),
          minWidth: minWidth(sortable),
        };
      default:
        return {
          element: createElement('span'),
          minWidth: minWidth(sortable),
        };
    }
  });
}

export function useFilteredOrganizationDevices(): IOrganizationDevice[] {
  const devices = useOrganizationDevices();
  const searchText = useOrganizationDevicesSearchText().toLowerCase();
  const sort = useOrganizationDevicesSort();

  const devicesSorted = useMemo(() => {
    if (sort.type === SortType.IDLE) {
      return devices;
    }

    return [...devices].sort((a: IOrganizationDevice, b: IOrganizationDevice) => {
      if (sort.type === SortType.ASC) {
        switch (sort.by) {
          case DevicesSortBy.SERIAL_NUMBER:
            return a.serialNumber.localeCompare(b.serialNumber);
          case DevicesSortBy.PATIENT:
            return getPatientName(a.patient).localeCompare(getPatientName(b.patient));
          default:
            return 0;
        }
      } else {
        switch (sort.by) {
          case DevicesSortBy.SERIAL_NUMBER:
            return b.serialNumber.localeCompare(a.serialNumber);
          case DevicesSortBy.PATIENT:
            return getPatientName(b.patient).localeCompare(getPatientName(a.patient));
          default:
            return 0;
        }
      }
    });
  }, [devices, sort]);

  const devicesFilteredBySearch: IOrganizationDevice[] = useMemo(() => {
    return devicesSorted.filter((device: IOrganizationDevice) => {
      const deviceDataToSearch = {
        serialNumber: device.serialNumber.toLowerCase(),
        patient: getPatientName(device.patient).toLowerCase(),
      };
      const values: string[] = Object.values(deviceDataToSearch);
      return values.some((value: string) => value.includes(searchText));
    });
  }, [devicesSorted, searchText]);

  return devicesFilteredBySearch;
}

export function useAnyOrganizationDevicesFiltersActive() {
  const organization = useOrganization();
  const [isAnyActive, setIsAnyActive] = useState(false);

  useEffect(() => {
    if (organization.searchText.devices !== '') {
      setIsAnyActive(true);
    } else {
      setIsAnyActive(false);
    }
  }, [organization.searchText.devices]);

  return isAnyActive;
}
